import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronLeft } from 'react-feather';
import ReCAPTCHA from 'react-google-recaptcha';

import { Button, Input, InputErrorMessage, InputWrapper, Label, MaskInput } from 'styles/Form';
import UserService from 'services/UserService';
import { RenderIf } from 'components/layout';
import * as S from '../styles';
import { isValidCpf } from 'utils/appUtils';
import PhoneAuthResendCode from '../PhoneAuthResendCode/PhoneAuthResendCode';

enum PHONE_REGISTER_VIEW {
	REGISTER_VIEW = 'REGISTER_VIEW',
	CODE_VIEW = 'CODE_VIEW',
}

const PhoneRegister = ({ showBackButton = true, backFn, registerSuccessFn }: any) => {
	const [currentView, setCurrentView] = useState(PHONE_REGISTER_VIEW.REGISTER_VIEW);

	const [isLoading, setIsLoading] = useState(false);

	const [cpf, setCpf] = useState({ value: '', hasError: false });
	const [name, setName] = useState({ value: '', hasError: false });
	const [phoneNumber, setPhoneNumber] = useState({ value: '', hasError: false });
	const [email, setEmail] = useState({ value: '', hasError: false });
	const [password, setPassword] = useState({ value: '', hasError: false });
	const [confirmPassword, setConfirmPassword] = useState({ value: '', hasError: false });

	const [apiKey, setApiKey] = useState('');
	const [code, setCode] = useState({ value: '', hasError: false });
	const [captchaToken, setCaptchaToken] = useState<string | null>(null);
	const captchaRef = useRef<ReCAPTCHA>(null);

	const [registerFormInvalid, setRegisterFormInvalid] = useState(true);

	useEffect(() => {
		const isFormInvalid = (
			!cpf.value || cpf.hasError ||
			!name.value || name.hasError ||
			!phoneNumber.value || phoneNumber.hasError ||
			!email.value || email.hasError ||
			!password.value || password.hasError ||
			!confirmPassword.value || confirmPassword.hasError ||
			password.value !== confirmPassword.value
		);
		setRegisterFormInvalid(isFormInvalid);
	}, [cpf, name, phoneNumber, email, password, confirmPassword]);

	const changeCpf = (ev: any) => {
		ev.persist();
		const cleanValue = ev.target.value.replace(/[^0-9]/g, '').substring(0, 11);
		setCpf(() => ({
			hasError: cleanValue.length > 0 && !isValidCpf(cleanValue),
			value: cleanValue
		}));
	}

	const changeName = (ev: any) => {
		ev.persist();
		const value = ev.target.value;
		const nameArray = value.split(' ').filter((name: string) => name.length > 0);
		setName(() => ({
			hasError: value.length === 0 || nameArray.length < 2,
			value: value
		}));
	}

	const changePhoneNumber = (ev: any) => {
		ev.persist();
		const cleanValue = ev.target.value.replace(/\D/g,'').substring(0, 11);
		setPhoneNumber(() => ({
			hasError: cleanValue.length === 0 || cleanValue.length < 11,
			value: cleanValue
		}));
	}

	const changeEmail = (ev: any) => {
		ev.persist();
		setEmail(() => ({
			hasError: ev.target.value.length === 0 || !ev.target.value.match(/^[\w\.\-_]{1,}@[\w\.\-]{6,}/),
			value: ev.target.value
		}));
	}

	const changePassword = (ev: any) => {
		ev.persist();
		setPassword(() => ({
			hasError: ev.target.value.length < 6,
			value: ev.target.value
		}));
		checkPasswords(ev.target.value);
	}

	const changeConfirmPassword = (ev: any) => {
		ev.persist();
		setConfirmPassword(() => ({
			hasError: ev.target.value.length < 6 || password.value !== ev.target.value,
			value: ev.target.value
		}));
	}

	const checkPasswords = (currentPassword: string) => {
		setConfirmPassword(old => ({
			...old,
			hasError: old.value.length < 6 || currentPassword !== old.value,
		}));
	}

	const changeCode = (ev: any) => {
		ev.persist();
		setCode(() => ({
			hasError: ev.target.value.length !== 6,
			value: ev.target.value
		}));
	}

	const submitRegister = (ev: any) => {
		ev.preventDefault();
		if (!captchaToken) {
			toast('Resolva o captcha antes de continuar', { type: toast.TYPE.WARNING });
			return;
		}
		setIsLoading(true);

		UserService.checkDocumentIsValid(cpf.value)
			.then(() => {
				return UserService.checkPhoneAndSendCode(phoneNumber.value, captchaToken!);
			})
			.then(({ data }) => {
				setApiKey(data);
				setCaptchaToken(null);
				captchaRef.current?.reset();
				setCurrentView(PHONE_REGISTER_VIEW.CODE_VIEW);
			})
			.catch(({ data }) => {
				toast(data.message, { type: toast.TYPE.ERROR });
				captchaRef.current?.reset();
				setCaptchaToken(null);
			})
			.finally(() => setIsLoading(false));
	}

	const resendSms = () => {
		UserService.resendSmsRegisterCode(phoneNumber.value, apiKey)
			.then(({ data }) => {
				setApiKey(data);
				toast('Código reenviado por SMS', { type: toast.TYPE.SUCCESS });
			})
			.catch(({ data }) => toast(data.message, { type: toast.TYPE.ERROR }));
	}

	const submitCode = (ev: any) => {
		ev.preventDefault();
		setIsLoading(true);
		const params = { phone: phoneNumber.value, code: code.value };

		UserService.validatePhoneRegisterCode(params, apiKey)
			.then(() => {
				const payload = {
					phone: phoneNumber.value,
					name: name.value,
					document: cpf.value,
					code: code.value,
					email: email.value,
					confirmEmail: email.value,
					password: password.value,
					birthDate: null,
					gender: null,
					userAddress: null,
				};

				return UserService.concludePhoneRegister(payload, apiKey);
			})
			.then(({ data }) => {
				toast(data.message, { type: toast.TYPE.SUCCESS });
				registerSuccessFn('PHONE');
			})
			.catch(({ data }) => toast(data.message, { type: toast.TYPE.ERROR }))
			.finally(() => setIsLoading(false));
	}

	const backView = () => {
		if (currentView === PHONE_REGISTER_VIEW.REGISTER_VIEW) {
			backFn();
			return;
		}
		setCurrentView(PHONE_REGISTER_VIEW.REGISTER_VIEW);
	}

	return (
		<>
			<S.Header>
				<RenderIf condition={showBackButton || currentView !== PHONE_REGISTER_VIEW.REGISTER_VIEW}>
					<S.ReturnButton onClick={backView}>
						<ChevronLeft size="1rem" />
					</S.ReturnButton>
				</RenderIf>
				<S.Title>Crie sua conta</S.Title>
			</S.Header>

			<RenderIf condition={currentView === PHONE_REGISTER_VIEW.REGISTER_VIEW}>
				<S.SmallText>Informe seus dados para criar sua conta</S.SmallText>
				<form onSubmit={submitRegister} style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "65vh"}}>
					<InputWrapper hasError={cpf.hasError}>
						<Label>CPF</Label>
						<MaskInput id="cpf" mask="999.999.999-99" value={cpf.value} maskPlaceholder={null} placeholder="000.000.000-00" onChange={changeCpf} />
						<InputErrorMessage>Campo inválido</InputErrorMessage>
					</InputWrapper>

					<InputWrapper hasError={name.hasError}>
						<Label data-required>Nome e sobrenome</Label>
						<Input id="name" placeholder="Insira seu nome e sobrenome" value={name.value} onChange={changeName} />
						<InputErrorMessage>Insira seu nome e sobrenome</InputErrorMessage>
					</InputWrapper>

					<InputWrapper hasError={phoneNumber.hasError}>
						<Label data-required>Telefone</Label>
						<MaskInput
							id="phoneNumber"
							mask="(99) 99999-9999"
							value={phoneNumber.value}
							maskPlaceholder={null}
							placeholder="(00) 00000-0000"
							onChange={changePhoneNumber} />
						<InputErrorMessage>Campo inválido</InputErrorMessage>
					</InputWrapper>

					<InputWrapper hasError={email.hasError}>
						<Label data-required>E-mail</Label>
						<Input id="email" placeholder="seunome@email.com" value={email.value} onChange={changeEmail} />
						<InputErrorMessage>Campo inválido</InputErrorMessage>
					</InputWrapper>

					<InputWrapper hasError={password.hasError}>
						<Label data-required>Senha</Label>
						<Input id="password" type="password" value={password.value} onChange={changePassword} />
						<InputErrorMessage>Senha deve ter no mínimo 6 caracteres</InputErrorMessage>
					</InputWrapper>

					<InputWrapper hasError={confirmPassword.hasError}>
						<Label data-required>Confirmar senha</Label>
						<Input id="confirmPassword" type="password" value={confirmPassword.value} onChange={changeConfirmPassword} />
						<InputErrorMessage>As senhas são diferentes</InputErrorMessage>
					</InputWrapper>

					<ReCAPTCHA
						ref={captchaRef}
						sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY || ''}
						onChange={(token: string | null) => setCaptchaToken(token)}
						onExpired={() => setCaptchaToken(null)}
					/>

					<S.TermsText>Ao me cadastrar, concordo com os <a href={`${process.env.REACT_APP_FAQ_URL}/termos-de-uso`} target="_blank">Termos de Uso</a> da plataforma</S.TermsText>

					<Button
						id="submitPhoneRegister"
						width="100%"
						margin="2.25rem 0 0"
						disabled={registerFormInvalid || !captchaToken}
						loading={isLoading}>Criar conta</Button>
				</form>
			</RenderIf>

			<RenderIf condition={currentView === PHONE_REGISTER_VIEW.CODE_VIEW}>
				<S.SmallText>Digite o código que você recebeu por SMS.</S.SmallText>
				<form onSubmit={submitCode}>
					<InputWrapper hasError={code.hasError}>
						<Label data-required>Código</Label>
						<MaskInput
							id="code"
							mask="999999"
							value={code.value}
							maskPlaceholder={null}
							placeholder="000000"
							onChange={changeCode} />
						<InputErrorMessage>Campo inválido</InputErrorMessage>
					</InputWrapper>

					<Button
						id="validatePhoneRegisterCode"
						width="100%"
						margin="2.25rem 0 0"
						disabled={code.value.length === 0 || code.hasError}
						loading={isLoading}>Verificar código</Button>
				</form>
				<PhoneAuthResendCode hasOptions={false} resendCode={resendSms} />
			</RenderIf>
		</>
	);
}

export default PhoneRegister;
